import { GraduationCap } from "lucide-react"
import { useState } from "react"

import { swatchClassFor } from "@/components/learn/masterclass-swatch"
import { MODULE_COVER_ASPECT_CLASS } from "@/lib/masterclass-module-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-833 — Resolved module-cover renderer, shared across the brand module
 * card, the module form preview, and the creator Learn list thumb. Renders
 * the attached cover `<img>` when a resolved URL is present, else falls back
 * to a deterministic colour swatch + `GraduationCap` glyph (`image || swatch`,
 * UX Spec §2.1).
 *
 * Ratio discipline (P0, LQE ratio guard): the `card` footprint is locked to
 * `MODULE_COVER_ASPECT_CLASS` (**3:2**) — this component NEVER imports the
 * 16:5 `MASTERCLASS_COVER_ASPECT_CLASS`. The module card is a mission-style
 * card, not a masterclass banner. The `thumb` footprint is a 56px square —
 * a center-square derivation of the card crop, deliberately excluded from
 * framed==shown (see `lib/masterclass-module-cover`).
 *
 * Swatch family: reuses `swatchClassFor(id)` from the masterclass swatch so
 * a given module maps to the SAME deterministic tone family as masterclass
 * covers (PRD §3) — only the footprint ratio differs. The swatch is
 * `aria-hidden`; the surrounding title/type/description carry the semantics.
 *
 * Error resilience (UX Spec §3): a broken/purged blob URL that fails to load
 * swaps back to the swatch via `onError` — a display surface never shows a
 * broken-image glyph.
 *
 * Testid contract (mirrors the shipped mission/masterclass cover pattern):
 * the base `data-testid` is suffixed per branch — `${testId}-image` on the
 * `<img>`, `${testId}-fallback` on the swatch.
 */
export type MasterclassModuleCoverSize = "card" | "thumb"

export interface MasterclassModuleCoverProps {
  /** Module id — drives the swatch fallback palette (Math.abs(id) % 6). */
  id: number
  /** Resolved cover URL (right variant chosen server-side) or null. */
  coverImageUrl: string | null
  /** Module title — used as the `<img>` alt text. */
  title: string | null
  /** `"card"` (3:2 band) or `"thumb"` (56px square) footprint. */
  size: MasterclassModuleCoverSize
  /** Optional override classes on the wrapper img/swatch. */
  className?: string
  "data-testid"?: string
}

export function MasterclassModuleCover({
  id,
  coverImageUrl,
  title,
  size,
  className,
  "data-testid": testId,
}: MasterclassModuleCoverProps) {
  const [errored, setErrored] = useState(false)

  const showImage = Boolean(coverImageUrl) && !errored

  const footprint =
    size === "card"
      ? `${MODULE_COVER_ASPECT_CLASS} w-full`
      : "h-14 w-14 shrink-0 rounded-md"

  if (showImage) {
    return (
      <img
        src={coverImageUrl!}
        alt={title ?? ""}
        onError={() => setErrored(true)}
        data-testid={testId ? `${testId}-image` : undefined}
        className={cn(footprint, "object-cover", className)}
      />
    )
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center",
        footprint,
        swatchClassFor(id),
        className,
      )}
      aria-hidden="true"
      data-testid={testId ? `${testId}-fallback` : undefined}
    >
      <GraduationCap
        className={cn("text-white/95", size === "card" ? "size-12" : "size-7")}
      />
    </div>
  )
}

export default MasterclassModuleCover
